// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  RegistrationExpiryRow,
  RegistrationExpirySearchFormValues,
  RegistrationExpirySummary,
} from "services/api/fleet/registrations/types";
import {
  getRegistrationExpiriesAPI,
  getRegistrationExpirySummaryAPI,
} from "services/api/fleet/registrations/api";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import useUrlFilters from "services/hooks/useUrlFilters";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { FLEET_REGISTRATION_EXPIRY_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/registrations/navigation";

// Lokalne komponente
import {
  RegistrationExpirySearchPageCmp,
  RegistrationExpirySearchPageProps,
} from "./types";
import RegistrationStatusTabs from "./components/RegistrationStatusTabs/RegistrationStatusTabs";
import RegistrationSearchFilters from "./components/RegistrationSearchFilters/RegistrationSearchFilters";
import RegistrationSearchResults from "./components/RegistrationSearchResults/RegistrationSearchResults";

const initialFilters: RegistrationExpirySearchFormValues = {
  search: "",
  expirationDateFrom: null,
  expirationDateTo: null,
  status: "",
  vehicleManufacturerId: null,
  vehicleModelId: null,
  vehicleTypeId: null,
};

const initialSummary: RegistrationExpirySummary = {
  expired: 0,
  expiring: 0,
  valid: 0,
  total: 0,
};

const RegistrationExpirySearchPage: RegistrationExpirySearchPageCmp = (
  _props: RegistrationExpirySearchPageProps
) => {
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<RegistrationExpiryRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  // Datumski raspon sinkroniziran s URL-om (drill-down s dashboarda + share/reload).
  const [filterValues, setFilterValues] = useUrlFilters<RegistrationExpirySearchFormValues>(
    initialFilters,
    {
      expirationDateFrom: { param: "expirationDateFrom", type: "date" },
      expirationDateTo: { param: "expirationDateTo", type: "date-end" },
    }
  );
  const [summary, setSummary] =
    useState<RegistrationExpirySummary>(initialSummary);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: RegistrationExpirySearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(await getRegistrationExpiriesAPI(pagination, values));
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchSummary = async () => {
    try {
      setSummary(await getRegistrationExpirySummaryAPI());
    } catch {
      setSummary(initialSummary);
    }
  };

  useEffect(() => {
    // Kreni s vrijednostima iz URL-a (useUrlFilters ih je već popunio) — ne resetiraj na initial.
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = useCallback(
    (values: RegistrationExpirySearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      const values = { ...filterValues, status };
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues, searchResults.pagination]
  );

  const handlePaginationChange = (pagination: Pagination) => {
    fetchSearchResult(pagination, filterValues);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_REGISTRATION_EXPIRY_SEARCH_PAGE_TITLE.text}
              icon={FLEET_REGISTRATION_EXPIRY_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_REGISTRATION_EXPIRY_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <RegistrationStatusTabs
            activeStatus={filterValues.status}
            summary={summary}
            onStatusChange={handleStatusChange}
          />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <RegistrationSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema vozila" />
          ) : (
            <RegistrationSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

RegistrationExpirySearchPage.route =
  Routes.RegistrationExpirySearchPageRouter;

export default RegistrationExpirySearchPage;
